<script>
  import { Link, useLocation, useMatch } from "svelte-navigator";
  const location = useLocation();
  /**
   * @type {string}
   */
  export let text = "Lorem";
  /**
   * @type {string}
   */
  export let href = "";
  export let keepParams = true;
  export let preventNavigation = false;
  export let reload = false;
  export let ellipsis = false;
  export let disabled = preventNavigation;

  const routeMatcher = useMatch(href);

  $: currentSearch = keepParams && $location?.search ? $location.search : "";
  $: isSelected = !!$routeMatcher && !preventNavigation;
  $: fullHref = `${href}${currentSearch}`;
</script>

<!-- 
    @component This component is used to create a side navigation item.
    The default slot is used for the icon, the element that always shows.
 -->

<div
  class="item"
  class:active={isSelected}
  class:disabled-item={disabled}
  class:ellipsis-item={ellipsis}
  data-label={text}
  on:click
  on:keydown
>
  {#if preventNavigation}
    <div class="side-nav-item">
      <div class="side-nav-item-icon">
        <slot />
      </div>
      <p
        class="side-nav-item-title"
        class:ellipsis
        title={ellipsis ? text : null}
      >
        {text}
      </p>
    </div>
  {:else if reload}
    <a href={fullHref} class="side-nav-item">
      <div class="side-nav-item-icon">
        <slot />
      </div>
      <p
        class="side-nav-item-title"
        class:ellipsis
        title={ellipsis ? text : null}
      >
        {text}
      </p>
    </a>
  {:else}
    <Link to={fullHref} class="side-nav-item">
      <div class="side-nav-item-icon">
        <slot />
      </div>
      <p
        class="side-nav-item-title"
        class:ellipsis
        title={ellipsis ? text : null}
      >
        {text}
      </p>
    </Link>
  {/if}
</div>

<style>
  .item {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  .item:active {
    background-color: var(--pax-orange);
    color: var(--pax-white);
    border-radius: 0.5rem;
  }

  .active {
    background: var(--pax-smoke);
    color: var(--pax-white);
    border-radius: 0.5rem;
    border: 1px solid var(--pax-black);
  }

  :global(.side-nav-item) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    gap: 0.5rem;
    color: var(--pax-black);
    text-decoration: none;
  }

  :global(.side-nav-item:hover) {
    background: var(--pax-smoke);
    color: var(--pax-black);
    border-radius: 0.5rem;
    font-weight: 400;
  }

  .disabled-item {
    opacity: 0.35;
    cursor: default;
  }

  .disabled-item :global(.side-nav-item) {
    cursor: default;
    pointer-events: none;
  }

  .disabled-item :global(img) {
    filter: grayscale(1);
  }

  :global(.side-nav-item:hover .svg-color) {
    fill: var(--pax-black);
  }

  :global(.side-nav-item:hover .svg-bg) {
    fill: transparent;
  }

  .side-nav-item-icon {
    height: 1.5rem;
    width: 1.5rem;
    flex: 0 0 1.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .side-nav-item-icon :global(.icon_wrapper) {
    width: 1.5rem !important;
    height: 1.5rem;
  }

  .side-nav-item-icon :global(img),
  .side-nav-item-icon :global(svg) {
    max-width: 1.25rem;
    max-height: 1.25rem;
    display: block;
  }

  .side-nav-item-title {
    display: none;
    text-transform: capitalize;
    font-size: 0.75rem;
    font-weight: 300;
    line-height: 0.8rem;
  }

  .ellipsis-item :global(.side-nav-item) {
    position: relative;
    overflow: hidden;
  }

  :global(.app-side-nav.expanded) .ellipsis-item {
    align-self: stretch;
  }

  :global(.app-side-nav.expanded) .ellipsis-item :global(.side-nav-item) {
    justify-content: flex-start;
    width: 100%;
  }

  .side-nav-item-title.ellipsis {
    position: absolute;
    left: 2rem;
    right: 0.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.app-side-nav.expanded) .side-nav-item-title.ellipsis {
    display: block;
  }

  :global(.app-side-nav:not(.expanded)) .item:hover::after {
    content: attr(data-label);
    position: absolute;
    left: calc(100% + 0.75rem);
    top: 50%;
    transform: translateY(-50%);
    z-index: var(--z100);
    background: var(--pax-black);
    color: var(--pax-white);
    border-radius: 0.25rem;
    padding: 0.4rem 0.55rem;
    font-size: 0.75rem;
    line-height: 1rem;
    white-space: nowrap;
    pointer-events: none;
  }

  :global(.app-side-nav:not(.expanded)) .item:hover::before {
    content: "";
    position: absolute;
    left: calc(100% + 0.4rem);
    top: 50%;
    transform: translateY(-50%);
    border: 0.35rem solid transparent;
    border-right-color: var(--pax-black);
    z-index: var(--z100);
    pointer-events: none;
  }
</style>
